/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */

import {Log} from "../utils/Log";
import {TimeoutForOnce, Once, UnLimited} from "./consts.js";

/**
 * Reply router for a single in-flight call (callId + callName).
 *
 * This object owns timeout and answer-count cleanup so callers don't leak listeners when remote peers misbehave.
 */
export class EmitterReplyListener {

    /**
     * Creates a new EmitterReplyListener instance.
     *
     * Semantics:
     * - Each emitter is bound to a single callId + callName.
     * - It forwards each received reply event to `callback`.
     * - It auto-removes itself when:
     *    - `answerLimit` replies have been received, OR
     *    - the timeout expires (when `answerLimit !== UnLimited`).
     *
     * IMPORTANT:
     * - `UnLimited` is a very large value used as a practical "no limit" sentinel.
     *   When answerLimit is UnLimited, the emitter is not automatically removed by timeout
     *   (timeoutId will be null).
     *
     * @param actor {Actor}
     * @param callId {string}
     * @param callName {string}
     * @param callback {function(ActorEvent)}
     * @param {number} [timeout=TimeoutForOnce]
     * @param {number} [answerLimit=Once]
     * @param {function():void|null} [onTimeout=null]
     */
    constructor(actor, callId, callName, callback, timeout = TimeoutForOnce, answerLimit = Once, onTimeout = null) {
        this.actor = actor;
        this.callId = callId;
        this.callName = callName;
        this.callback = callback;
        this.timeout = timeout;
        this.answerLimit = answerLimit;
        this.counter = 0;
        this.onTimeout = onTimeout;

        if (this.answerLimit !== UnLimited) {
            // Timeout-driven cleanup is skipped for "unlimited" streams to avoid silently terminating long-lived subscriptions.
            this.timeoutId = setTimeout(() => {
                try {
                    if (typeof this.onTimeout === "function") {
                        this.onTimeout();
                    }
                } catch (e) {
                    Log.error(`[EmitterReplyListener.onTimeout] Error: ${e.message}`);
                } finally {
                    this.actor.removeEmitter(this);
                }
            }, timeout);
        } else {
            this.timeoutId = null;
        }

    }


    /**
     * Handles a reply event from the emitter.
     * @param {ActorEvent} event
     */
    handleReply(event) {
        if (Log.supports(Log.Dev)) {
            // Avoid JSON.stringify costs unless the dev log level is enabled.
            Log.dev('[EmitterReplyListener.handleReply] ', event.type, event.callId, JSON.stringify(event.detail), event.isError());
        }
        try {
            this.callback(event);
        } catch (e) {
            Log.error(`[EmitterReplyListener.handleReply] Error handling reply: ${e.message}`);
        }
        this.counter++;
        if (this.answerLimit !== UnLimited && this.counter >= this.answerLimit) {
            this.actor.removeEmitter(this);
        }
    }
}